import { Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  getDashboardQuestionnaireStatus,
  getQuestionnaires,
} from "../../repository/data.repository";
import { DashboardQuestionnaireStatus } from "../../repository/data.default";
import { createSearchRequest } from "../../hooks/useSearchRequest";
import { renderMetricChart, renderRateBar, renderShareRing } from "./MetricChartRender";
import { InsightChartCard, InsightStrip } from "./InsightCards";
import { pickBucket } from "./AnalyticsRange";
import { DashboardSectionScope } from "./DashboardSection";
import RemoteSelect from "./RemoteSelect";

const questionnaireSearch = createSearchRequest(getQuestionnaires, "name");

// Assessments at a glance from one server-side rollup: submission volume and completion over
// time, the overall status split, which questionnaires carry the activity (as a share ring),
// and — platform-wide — how much of each company's registered population the assessments
// actually reach (as worst-first rate bars). A questionnaire selector narrows the whole card
// to one instrument; the section filter bar handles company/department/date.
function AssessmentsCard(props: DashboardSectionScope) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState<DashboardQuestionnaireStatus | undefined>();
  const [questionnaireUid, setQuestionnaireUid] = useState<string | undefined>();

  const from = props.range?.[0];
  const to = props.range?.[1];
  const bucket = pickBucket(from, to);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getDashboardQuestionnaireStatus({
      companyUid: props.companyUid,
      departmentUid: props.departmentUid,
      questionnaireUid,
      from,
      to,
      bucket,
    })
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [props.companyUid, props.departmentUid, questionnaireUid, from, to, bucket]);

  const total = status?.total ?? 0;
  const completed = status?.completed ?? 0;
  const completionRate = total > 0 ? Math.round((completed / total) * 1000) / 10 : 0;

  const series = status?.series ?? [];
  const submissionsTrend = Object.fromEntries(
    series.map((p) => [dayjs(p.date).format("YYYY-MM-DD"), p.total]),
  );
  // Completed share per bucket, skipping empty buckets (no submissions = no rate to show).
  const completionTrend = Object.fromEntries(
    series
      .filter((p) => p.total > 0)
      .map((p) => [
        dayjs(p.date).format("YYYY-MM-DD"),
        Math.round((p.completed / p.total) * 1000) / 10,
      ]),
  );
  const statusSlices = [
    { type: "Completed", value: completed },
    { type: "In progress", value: status?.inProgress ?? 0 },
  ].filter((s) => s.value > 0);
  // Named top instruments only — the share ring appends the honest "Other" remainder itself.
  const byQuestionnaire = (status?.byQuestionnaire ?? []).map((q) => ({
    type: q.label,
    value: q.total,
  }));
  const coverage = (status?.coverageByCompany ?? []).map((c) => ({
    label: c.label,
    rate: c.rate,
  }));
  const activeBucket = status?.bucket ?? bucket;

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Typography.Text type="secondary" className="text-xs">
            Questionnaire
          </Typography.Text>
          <RemoteSelect
            className="w-64"
            placeholder="All questionnaires"
            value={questionnaireUid}
            onChange={setQuestionnaireUid}
            request={questionnaireSearch}
          />
        </div>
      </div>
      <InsightStrip
        items={[
          `${total.toLocaleString()} submissions`,
          `${completed.toLocaleString()} completed`,
          `${completionRate}% completion`,
          `${(status?.respondents ?? 0).toLocaleString()} respondents`,
        ]}
      />
      <div className="flex flex-row flex-wrap gap-3">
        <InsightChartCard
          title="Submissions"
          subtitle={`By ${activeBucket}`}
          loading={loading}
          error={error}
        >
          {renderMetricChart("line", submissionsTrend, [], "Submissions", 260)}
        </InsightChartCard>
        <InsightChartCard
          title="Completion rate"
          subtitle={`Completed share of submissions, by ${activeBucket}`}
          loading={loading}
          error={error}
        >
          {renderMetricChart("line", completionTrend, [], "% completed", 260, [0, 100])}
        </InsightChartCard>
        <InsightChartCard title="Questionnaires status" loading={loading} error={error}>
          {renderMetricChart("pie", {}, statusSlices, "Questionnaires", 260)}
        </InsightChartCard>
        <InsightChartCard
          title="By questionnaire"
          subtitle="Share of submissions by instrument — top 10 named, the rest as Other"
          loading={loading}
          error={error}
        >
          {renderShareRing(byQuestionnaire, total, 260)}
        </InsightChartCard>
        {!props.companyUid && (
          <InsightChartCard
            title="Coverage by company"
            subtitle={`Lowest 10 of ${status?.coverageTotalCompanies ?? 0} companies — share of registered users who have submitted anything`}
            loading={loading}
            error={error}
          >
            {renderRateBar(coverage, 260)}
          </InsightChartCard>
        )}
      </div>
    </div>
  );
}

export default AssessmentsCard;
